import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { INDUSTRIES, SERVICES } from "@/data/site";
import { GlowOrb } from "@/components/common/GlowOrb";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/seo/Seo";

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((x) => x.slug === slug);
  if (!ind) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-40 text-center" data-testid="industry-not-found">
        <Seo title="Industry not found" noindex />
        <h1 className="font-heading font-bold text-3xl">Industry not found</h1>
        <Link to="/industries" className="mt-4 inline-block text-primary">Back to industries</Link>
      </div>
    );
  }
  const Icon = Icons[ind.icon] || Icons.Layers;
  const useCases = [
    "AI voice agent handling inbound and outbound calls in native languages",
    "Custom copilots trained on internal SOPs and knowledge bases",
    "Automated document processing (contracts, invoices, forms)",
    "Predictive analytics dashboards for leadership",
    "White-labelled customer-facing AI apps",
  ];

  return (
    <div data-testid="industry-detail-page">
      {/* Generic template pages stay out of the index until they get original, industry-specific content. */}
      <Seo title={`${ind.name} Software Development`} description={ind.blurb} robots="noindex, follow" />
      <section className="relative overflow-hidden py-20 md:py-28">
        <GlowOrb className="top-0 left-0" color="primary" size={500} />
        <div className="relative max-w-7xl mx-auto px-6">
          <Link to="/industries" className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">← All industries</Link>
          <div className="mt-6 flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icon className="w-7 h-7" />
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Industry</div>
          </div>
          <h1 className="mt-6 font-heading font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            AI for <span className="text-gradient-aurora">{ind.name}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">{ind.blurb}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-4">
          <div className="glass rounded-3xl p-8">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-4">Use cases</div>
            <ul className="space-y-3">
              {useCases.map((u) => (
                <li key={u} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm">{u}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="glass rounded-3xl p-8">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-4">Related services</div>
            <div className="grid grid-cols-2 gap-2">
              {SERVICES.slice(0, 6).map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="px-3 py-2 rounded-xl border border-border hover:border-primary/50 text-sm">
                  {s.name}
                </Link>
              ))}
            </div>
            <Link to="/quote" className="mt-8 block">
              <button className="w-full rounded-full px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold btn-neon inline-flex items-center justify-center gap-2">
                Discuss your project <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
